/**
 * SearchInput Component
 * Debounced search input with clear button.
 * 
 * Usage:
 *   <SearchInput
 *     value={search}
 *     onChange={setSearch}
 *     placeholder="Search employees..."
 *     debounce={300}
 *   />
 */
import { useState, useEffect, useCallback } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Search, X } from 'lucide-react';

export function SearchInput({
  value = '',
  onChange,
  placeholder = 'Search...',
  debounce = 300,
  className = '',
  autoFocus = false,
  disabled = false
}) {
  const [localValue, setLocalValue] = useState(value);

  // Sync external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounced onChange
  const onChangeCallback = useCallback((val) => {
    onChange?.(val);
  }, [onChange]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localValue !== value) {
        onChangeCallback(localValue);
      }
    }, debounce);

    return () => clearTimeout(timer);
  }, [localValue, debounce, value, onChangeCallback]);

  const handleClear = () => {
    setLocalValue('');
    onChange?.('');
  };

  return (
    <div className={`relative ${className}`}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder={placeholder}
        className="pl-10 pr-10"
        autoFocus={autoFocus}
        disabled={disabled}
      />
      {localValue && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
          onClick={handleClear}
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

export default SearchInput;
